import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useApplicationConfig } from '~/data/applicationConfig';
import { OrganizationUnit, useAllOrgUnits } from '~/data/orgunits';
import { UnitWorkflowConfigs, useWorkflows } from '~/data/workflows';
import { useVisible } from '~/hooks/useVisible';

export const useWorkflowConfigurationView = () => {
  const { t } = useTranslation();
  const { applicationConfig } = useApplicationConfig();
  const workflows2EngineEnabled =
    applicationConfig.fComConfig?.miscellaneousConfig
      .fComEnableNewWorkflowsEngine || false;
  const unitWorkflowsEnabled =
    applicationConfig.fComConfig?.miscellaneousConfig
      .fComEnableWorkflowsByUnit || false;

  const { orgUnits, hasData, isLoading } = useAllOrgUnits();
  const { getUnitWorkflowConfigs, unitIdsWithWorkflows } = useWorkflows();

  const unitSelectorVisible = useVisible(false);
  const [selectedUnitWorkflows, setSelectedUnitWorkflows] =
    useState<UnitWorkflowConfigs>();
  const [selectedUnit, setSelectedUnit] = useState<OrganizationUnit>();

  const globalUnit: OrganizationUnit = useMemo(() => {
    return {
      fOrgUnitId: -1,
      fOrgUnitName: applicationConfig.fComName,
      fOrgUnitNumber: t('unitGlobal'),
      fOrgUnitType: '',
      fOrgUnitLoc: 0,
      fOrgUnitUrl: '',
      fOrgUnitCreated: '',
      fOrgUnitAvail: '',
      fOrgUnitManager: 0,
      fOrgUnitContainingTrees: [],
      fOrgUnitCostCenters: [],
      fOrgUnitResponsibleEmps: [],
      fOrgUnitResponsibleHrEmps: [],
      fOrgUnitPos: [],
    };
  }, [t, applicationConfig]);

  const handleSetSelectedOrgUnit = useCallback(
    (orgUnit: OrganizationUnit) => {
      setSelectedUnit(orgUnit);
      getUnitWorkflowConfigs(orgUnit.fOrgUnitId).then((unitWorkflowConfigs) => {
        setSelectedUnitWorkflows(unitWorkflowConfigs);
      });
    },
    [setSelectedUnit, getUnitWorkflowConfigs],
  );

  const handleSetSelectedUnitById = useCallback(
    (unitId: number) => {
      if (unitId === -1) {
        handleSetSelectedOrgUnit(globalUnit);
      } else {
        const foundUnit = orgUnits.find((unit) => unit.fOrgUnitId === unitId);
        if (!!foundUnit) {
          handleSetSelectedOrgUnit(foundUnit);
        }
      }
    },
    [handleSetSelectedOrgUnit, globalUnit, orgUnits],
  );

  useEffect(() => {
    if (!selectedUnit && hasData && !!globalUnit) {
      handleSetSelectedOrgUnit(globalUnit);
    }
  }, [hasData, selectedUnit, handleSetSelectedOrgUnit, globalUnit]);

  return {
    orgUnits,
    hasData,
    isLoading,
    globalUnit,
    selectedUnitWorkflows,
    configuredUnitIds: unitIdsWithWorkflows,
    unitSelector: {
      enabled: unitWorkflowsEnabled && hasData,
      ...unitSelectorVisible,
      selectedOrgUnit: selectedUnit,
      selectedUnitId: selectedUnit?.fOrgUnitId,
      selectUnitById: handleSetSelectedUnitById,
    },
    engineStatus: workflows2EngineEnabled,
  };
};
